import React from 'react';
import { Link } from '@tanstack/react-router';
import { toast } from 'react-toastify';

/**
 * UserCard - A reusable component for displaying user/author profiles
 *
 * @param {Object} props
 * @param {string} props.userId - User ID (required)
 * @param {string} props.userName - Display name (required)
 * @param {string} props.avatarUrl - Avatar image URL (required)
 * @param {string} props.bannerUrl - Banner image URL (required)
 * @param {string} props.bio - User bio/description (optional)
 * @param {string|React.ReactNode} props.role - User role/title (optional)
 * @param {boolean} props.showFollowButton - Whether to show follow button (default: true)
 * @param {function} props.onFollow - Callback when follow button is clicked (optional)
 * @param {string} props.cardClassName - Additional CSS classes for card
 * @param {string} props.bannerClassName - Additional CSS classes for banner image
 * @param {string} props.avatarClassName - Additional CSS classes for avatar image
 */
function UserCard({
  userId,
  userName,
  avatarUrl,
  bannerUrl,
  bio,
  role,
  showFollowButton = true,
  onFollow,
  cardClassName = '',
  bannerClassName = '',
  avatarClassName = ''
}) {
  // Default fallback images
  const defaultBanner = "https://cdn.stealthisnft.xyz/u/0/profile/banner.jpg";
  const defaultAvatar = "https://cdn.stealthisnft.xyz/u/0/profile/avatar.jpg";

  const handleImageError = (e, type) => {
    e.target.onError = null;
    if (type === 'banner') {
      e.target.src = defaultBanner;
      if (e.target.style) {
        e.target.style.backgroundImage = `url(${defaultBanner})`;
      }
    } else {
      e.target.src = defaultAvatar;
    }
  };

  const handleFollow = (e, userIdToFollow, userNameToFollow) => {
    e.preventDefault();
    e.stopPropagation();

    if (onFollow) {
      onFollow(userIdToFollow, userNameToFollow);
    } else {
      // Default follow behavior with toast
      const CustomToastWithLink = () => (
        <div>
          You are now following {userNameToFollow}<br />
          <Link to={"/profile/"+userIdToFollow}>Profile</Link>
        </div>
      );
      toast.info(CustomToastWithLink);
    }
  };

  if (!userId || !userName || !avatarUrl || !bannerUrl) {
    console.warn('UserCard: Missing required props', { userId, userName, avatarUrl, bannerUrl });
    return null;
  }

  return (
    <div className={`col-xl-3 col-md-6 mb-xl-0 ${cardClassName}`}>
      <div className="card card-blog card-purple">
        {/* Banner Image */}
        <div className="position-relative">
          <Link
            to={"/profile/"+userId}
            className="d-block border-radius-xl"
          >
            <img
              id={`banner_${userId}_${Math.random()}`}
              src={bannerUrl}
              alt={`${userName}'s banner`}
              className={`aspect11 img-fluid shadow border-radius-xl ${bannerClassName}`}
              style={{ objectFit: 'cover', overflow: 'hidden', minHeight: '100px', width: '100%' }}
              onError={(e) => handleImageError(e, 'banner')}
            />
          </Link>
        </div>

        {/* Avatar - overlapped */}
        <Link
          to={"/profile/"+userId}
          className="item-owner v2"
          data-bs-toggle="tooltip"
          data-bs-placement="bottom"
          title="View profile"
        >
          <img
            id={`avatar_${userId}_${Math.random()}`}
            src={avatarUrl}
            alt={userName}
            className={`aspect11 ${avatarClassName}`}
            onError={(e) => handleImageError(e, 'avatar')}
          />
        </Link>

        {/* Card Body */}
        <div className="item-cont card-body px-1 pb-0 text-center">
          <Link className="text-decoration-none" to={"/profile/"+userId}>
            <div className="mt-1 text-light text-bold mb-0 pb-0">
              {userName}
            </div>
            {role && (
              <div className="text-success text-sm">
                {role}
              </div>
            )}
          </Link>

          {bio && (
            <div className="author-card mb-2 mt-2 text-info text-sm">
              {bio}
            </div>
          )}

          {showFollowButton && (
            <div className="d-flex align-items-center justify-content-between">
              <Link
                to="#"
                onClick={(e) => handleFollow(e, userId, userName)}
                className="btn btn-outline-primary btn-sm mb-0 w-100"
              >
                Follow Now
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserCard;